import type {
  ComponentPropsWithoutRef,
  FunctionComponent,
  ReactNode,
} from 'react'
import React from 'react'

import classnames from 'classnames'

import { NavigationLink } from '../Links/NavigationLink'
import { ScrollControls } from '../ScrollControls'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './CardRow.module.scss'
import { useMeasurements } from './CardRow.utils'

export const CardRow: FunctionComponent<Props> = ({
  className,
  title,
  link,
  children,
  whichID,
  includeGutters = true,
  includeControls = true,
  includeScrollbar = true,
  ...rest
}) => {
  const { isScrollable, updateScroller, refs, scrollPosition, isScrolling } =
    useMeasurements()

  if (!children) {
    return null
  }

  return (
    <section
      ref={refs.wrapper}
      className={classnames(
        styles.cardRowWrapper,
        includeGutters && styles.withGutter,
        className
      )}
      data-testid="card-row-wrapper"
      {...rest}
    >
      {title && (
        <TypographyV2
          tag="h2"
          textStyle="sb-text-heading-large"
          className={styles.cardRowTitle}
          data-testid="card-row-title"
        >
          {title}
        </TypographyV2>
      )}

      <div className={styles.controlsWrapper}>
        <div
          className={classnames(
            styles.cardRowScrollWrapper,
            'sb-scrollbar-default',
            !includeScrollbar && styles.noScrollbar
          )}
          ref={refs.scroller}
        >
          <div className={styles.cardRowScroll}>{children}</div>
        </div>

        {isScrollable && includeControls && (
          <ScrollControls
            buttonLabel="article card"
            buttonClassNames={{
              left: classnames(
                styles.buttonPrevious,
                isScrolling && styles.nonInteractive
              ),
              right: classnames(
                styles.buttonNext,
                isScrolling && styles.nonInteractive
              ),
            }}
            clickHandler={(direction) => {
              updateScroller(direction)
            }}
            scrollPosition={scrollPosition}
            data-testid="card-scroll-controls"
          />
        )}
      </div>

      {link && (
        <NavigationLink
          href={link.href}
          key={link.text}
          className={styles.cardRowViewAll}
          data-testid="card-row-link"
          data-which-id={whichID}
        >
          {link.text}
        </NavigationLink>
      )}
    </section>
  )
}

///////// IMPLEMENTATION /////////

export type Props = {
  /**
   * Text to be added as block title, required
   */
  title?: string | ReactNode
  /**
   * Class name to be added to component for extra styling purposes
   */
  className?: string
  /**
   * Text to be added as block title, required
   */
  link?: { text: string; href: string }
  /**
   * Text to be added as block title, required
   */
  children?: ReactNode[] | ReactNode
  /**
   * Text to determine link data-which-id attribute
   */
  whichID?: string
  /**
   * Include the grid gutter margins (not advised)
   */
  includeGutters?: boolean
  /**
   * Include control navigation buttons
   */
  includeControls?: boolean
  /**
   * Include scrollbar
   */
  includeScrollbar?: boolean
} & ComponentPropsWithoutRef<'section'>
